import { GoBackLinkButton } from "../../components/GoBackLinkButton";
import { routes } from "../../appRoutingHelpers";
import UserProfileAvatarPartial from "./user-profile/UserProfileAvatarPartial";
import UserProfileDetailsPartial from "./user-profile/UserProfileDetailsPartial";
import UserProfileVoucherListPartial from "./user-profile/UserProfileVoucherListPartial";
import { UserMembercards } from "./user-profile/user-membercards/UserMembercards";
import { UserProfileBalancePartial } from "./user-profile/UserProfileBalancePartial";
import { UserProfileRolesPartial } from "./user-profile/UserProfileRolesPartial";
import { UserChallenges } from "./user-profile/user-challenges/UserChallenges";
import { UserOrders } from "./user-profile/user-orders/UserOrders";
import { useParams } from "react-router-dom";
import {
  useGetUserBalances,
  useGetUserProfile,
  useWithdrawBalance,
} from "../../service/api";
import { SpinnerBlock } from "../common/Spinner";
import { useSelector } from "react-redux";
import * as organizationSelectors from "../organization/organizationSelectors";
import { getCountrySpecificValues } from "../../util";
import { UserBalance } from "../../service/api-response/UserBalanceSuccessResponse";
import * as authSelectors from "../auth/authSelectors";
import { toastr } from "react-redux-toastr";
import { getErrorMessage, useHandleFetchError } from "../../error-handler";

export const UserProfile = () => {
  const { id } = useParams<{
    id: string;
  }>();
  const organizationKeyName = useSelector(
    authSelectors.selectedOrganizationKeyName
  );
  const { countryCode, currency } = getCountrySpecificValues();
  const mixpanelLink = useSelector(organizationSelectors.mixpanelLink);
  const isBalanceVisible = useSelector(
    organizationSelectors.isUserProfileBalanceVisible
  );

  const {
    data: userProfile,
    isLoading,
    error: userProfileError,
  } = useGetUserProfile(organizationKeyName, id);

  const {
    data: balances,
    isLoading: isLoadingBalance,
    error: balancesError,
  } = useGetUserBalances(organizationKeyName, id);

  const {
    mutateAsync: withdrawBalance,
    isLoading: isWithdrawingBalance,
  } = useWithdrawBalance(id);

  const onWithdrawBalanceClick = async (balance: UserBalance) => {
    if (!balance) {
      return alert("User has no balance");
    }

    const amountToRefund = balance.refundable;

    if (amountToRefund === 0) {
      return alert("User refundable amount is 0.");
    }

    const multiplier = balance.multiplier || 3;

    if (
      !window.confirm(
        `This will withdraw ${amountToRefund / 100} kr equivalent to ` +
          `${Number(amountToRefund / 100 / multiplier).toFixed(
            2
          )} real kr. Do You want to proceed?`
      )
    ) {
      return;
    }

    try {
      await withdrawBalance({
        organizationKeyName,
        userId: id,
        countryCode: balance.countryCode,
      });
      toastr.success("Success", "Balance withdrawn successfully");
    } catch (error: unknown) {
      const errorMessage = getErrorMessage(error);
      toastr.error("Error", errorMessage);
    }
  };

  const fallbackBalances: UserBalance[] = [
    {
      _type: "user-balance",
      balance: 0,
      cashbackBalance: 0,
      id: "",
      refundable: 0,
      subscriptionBonusBalance: 0,
      systemBonusBalance: 0,
      countryCode: countryCode,
      currency: currency,
      multiplier: 1,
      title: organizationKeyName,
    },
  ];
  const balancesList =
    balances && balances?.length > 0 ? balances : fallbackBalances;

  useHandleFetchError(userProfileError);
  useHandleFetchError(balancesError);

  if (isLoading || !userProfile) {
    return <SpinnerBlock />;
  }

  return (
    <div className="container mt-5">
      <GoBackLinkButton
        to={routes.userList(organizationKeyName)}
        text="Back to all users"
      />
      <div className="row">
        <div className="col-12 col-md-5">
          <div>
            <UserProfileAvatarPartial
              userProfile={userProfile?.userProfile}
              organization={organizationKeyName}
              mixpanelLink={mixpanelLink}
            />
            <UserProfileDetailsPartial userProfile={userProfile.userProfile} />
          </div>
        </div>
        <div className="col-12 col-md-7">
          <UserProfileVoucherListPartial
            userId={userProfile.userProfile.id}
            organization={organizationKeyName}
          />
          <UserMembercards />
          {isBalanceVisible && (
            <>
              {balancesList.map((balance, i) => (
                <UserProfileBalancePartial
                  key={i}
                  balance={balance}
                  isLoadingBalance={isLoadingBalance}
                  isWithdrawingBalance={isWithdrawingBalance}
                  onWithdrawBalanceClick={onWithdrawBalanceClick}
                />
              ))}
            </>
          )}
          <UserProfileRolesPartial userProfile={userProfile.userProfile} />
          <UserChallenges userId={userProfile.userProfile.id} />
          <UserOrders userId={userProfile.userProfile.id} />
        </div>
      </div>
    </div>
  );
};
